import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUtmTaxonomyStore, type Impact } from '@/store/utm-taxonomy-store';
import UtmDataTable from '@/components/utm/shared/UtmDataTable';
import { Plus, Edit, Trash2 } from 'lucide-react';

const UtmImpactList = () => {
  const navigate = useNavigate();
  const { impacts, fetchImpacts, deleteImpact, isLoading } = useUtmTaxonomyStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [impactToDelete, setImpactToDelete] = useState<string | null>(null);

  useEffect(() => {
    fetchImpacts();
  }, [fetchImpacts]);

  const filteredImpacts = impacts.filter((impact) => {
    const query = searchQuery.toLowerCase();
    return (
      impact.name.toLowerCase().includes(query) ||
      impact.description?.toLowerCase().includes(query) ||
      impact.level.toLowerCase().includes(query)
    );
  });

  const handleDelete = async () => {
    if (!impactToDelete) return;

    try {
      await deleteImpact(impactToDelete);
      setDeleteModalOpen(false);
      setImpactToDelete(null);
    } catch (error) {
      console.error('Failed to delete impact:', error);
      alert('Failed to delete impact. It may be attached to zones.');
    }
  };

  const openDeleteModal = (id: string) => {
    setImpactToDelete(id);
    setDeleteModalOpen(true);
  };

  const getLevelBadge = (level: string) => {
    const badges: Record<string, { color: string; label: string }> = {
      NOTAM: { color: 'bg-destructive text-destructive-foreground', label: 'NOTAM' },
      AUTHORISATION: { color: 'bg-destructive text-destructive-foreground', label: 'Authorisation' },
      NOTIFICATION: { color: 'bg-primary/20 text-primary', label: 'Notification' },
      NOTHING_TO_DO: { color: 'bg-muted text-muted-foreground', label: 'Informational' },
    };
    const badge = badges[level] || { color: 'bg-muted text-foreground', label: level };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  const getStatusBadge = (status: string) => (
    <span
      className={`px-2 py-1 rounded text-xs font-medium ${
        status === 'CHECKED' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'
      }`}
    >
      {status}
    </span>
  );

  const columns = [
    {
      key: 'name',
      label: 'Name',
      render: (impact: Impact) => (
        <div>
          <div className="font-medium text-foreground">{impact.name}</div>
          {impact.description && (
            <div className="text-xs text-muted-foreground mt-1">{impact.description}</div>
          )}
        </div>
      ),
    },
    {
      key: 'scope',
      label: 'Linked To',
      render: (impact: Impact) => (
        <span className="text-muted-foreground">
          {impact.linkType} ({impact.linkedIds?.length ?? 0})
        </span>
      ),
    },
    {
      key: 'level',
      label: 'Level',
      render: (impact: Impact) => getLevelBadge(impact.level),
    },
    {
      key: 'status',
      label: 'Status',
      render: (impact: Impact) => getStatusBadge(impact.status),
    },
  ];

  return (
    <div className="mx-auto max-w-7xl p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Impacts</h1>
          <p className="text-muted-foreground mt-1">Manage UTM regulatory impacts and requirements</p>
        </div>
        <button
          onClick={() => navigate('/admin/uzamaps/impacts/new')}
          className="flex w-full items-center justify-center space-x-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground transition-colors hover:bg-primary/90 sm:w-auto"
        >
          <Plus size={18} />
          <span>Add Impact</span>
        </button>
      </div>

      {/* Data Table */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="text-muted-foreground">Loading impacts...</div>
        </div>
      ) : (
        <UtmDataTable
          data={filteredImpacts}
          columns={columns}
          searchPlaceholder="Search impacts..."
          onSearch={setSearchQuery}
          itemsPerPage={10}
          mobileCardRender={(impact) => (
            <div className="space-y-3">
              <div>
                <div className="font-semibold text-foreground">{impact.name}</div>
                {impact.description && (
                  <div className="mt-1 text-xs text-muted-foreground">{impact.description}</div>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                {getLevelBadge(impact.level)}
                {getStatusBadge(impact.status)}
                <span className="text-muted-foreground">
                  {impact.linkType} ({impact.linkedIds?.length ?? 0})
                </span>
              </div>
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() => navigate(`/admin/uzamaps/impacts/${impact.id}/edit`)}
                  className="rounded p-2 text-primary transition-colors hover:bg-accent hover:text-primary/80"
                  title="Edit"
                >
                  <Edit size={16} />
                </button>
                <button
                  onClick={() => openDeleteModal(impact.id)}
                  className="rounded p-2 text-destructive transition-colors hover:bg-accent hover:text-destructive/80"
                  title="Delete"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          )}
          actions={(impact) => (
            <>
              <button
                onClick={() =>
                  navigate(`/admin/uzamaps/impacts/${impact.id}/edit`)
                }
                className="p-2 text-primary hover:text-primary/80 hover:bg-accent rounded transition-colors"
                title="Edit"
              >
                <Edit size={16} />
              </button>
              <button
                onClick={() => openDeleteModal(impact.id)}
                className="p-2 text-destructive hover:text-destructive/80 hover:bg-accent rounded transition-colors"
                title="Delete"
              >
                <Trash2 size={16} />
              </button>
            </>
          )}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-foreground mb-2">Delete Impact</h3>
            <p className="text-muted-foreground mb-6">
              Are you sure you want to delete this impact? This action cannot be undone. This
              impact will be removed from all zones.
            </p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setDeleteModalOpen(false);
                  setImpactToDelete(null);
                }}
                className="px-4 py-2 bg-muted hover:bg-accent text-foreground rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-destructive hover:bg-destructive/90 text-destructive-foreground rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UtmImpactList;
